import React, { useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { UserPlus, Loader2 } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function PartnerAddStudent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    currentEducationLevel: '',
    fieldOfStudy: '',
    gpa: '',
    graduationYear: '',
    institution: '',
    ielts: '',
    toefl: '',
    preferredCountries: '',
    preferredDegreeLevel: '',
    budget: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast.error('Email, First Name, and Last Name are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        preferredCountries: formData.preferredCountries
          ? formData.preferredCountries.split(',').map(s => s.trim())
          : [],
        graduationYear: formData.graduationYear ? parseInt(formData.graduationYear) : undefined
      };

      const response = await partnerAPI.createStudent(payload);
      toast.success('Student profile created successfully!');
      navigate('/partner/students');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create student profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PartnerLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Add New Student</h1>
          <p className="mt-1 text-slate-600">Create a student profile to apply for services on their behalf</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="partner-card mb-6 rounded-[28px] p-6">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="student@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="e.g. India"
                />
              </div>
            </div>
          </Card>

          {/* Academic Information */}
          <Card className="partner-card mb-6 rounded-[28px] p-6">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Academic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="currentEducationLevel">Education Level</Label>
                <select
                  id="currentEducationLevel"
                  name="currentEducationLevel"
                  value={formData.currentEducationLevel}
                  onChange={handleChange}
                  className="w-full rounded-2xl px-3 py-2 text-sm"
                >
                  <option value="">Select level</option>
                  <option value="bachelor">Bachelor's</option>
                  <option value="master">Master's</option>
                  <option value="phd">PhD</option>
                  <option value="diploma">Diploma</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="fieldOfStudy">Field of Study</Label>
                <Input
                  id="fieldOfStudy"
                  name="fieldOfStudy"
                  value={formData.fieldOfStudy}
                  onChange={handleChange}
                  placeholder="e.g. Computer Science"
                />
              </div>
              <div>
                <Label htmlFor="institution">Institution</Label>
                <Input
                  id="institution"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  placeholder="Current university/college"
                />
              </div>
              <div>
                <Label htmlFor="gpa">GPA</Label>
                <Input
                  id="gpa"
                  name="gpa"
                  value={formData.gpa}
                  onChange={handleChange}
                  placeholder="e.g. 3.5/4.0"
                />
              </div>
              <div>
                <Label htmlFor="graduationYear">Graduation Year</Label>
                <Input
                  id="graduationYear"
                  name="graduationYear"
                  type="number"
                  value={formData.graduationYear}
                  onChange={handleChange}
                  placeholder="e.g. 2025"
                />
              </div>
            </div>
          </Card>

          {/* Test Scores */}
          <Card className="partner-card mb-6 rounded-[28px] p-6">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Test Scores</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="ielts">IELTS</Label>
                <Input
                  id="ielts"
                  name="ielts"
                  value={formData.ielts}
                  onChange={handleChange}
                  placeholder="e.g. 7.0"
                />
              </div>
              <div>
                <Label htmlFor="toefl">TOEFL</Label>
                <Input
                  id="toefl"
                  name="toefl"
                  value={formData.toefl}
                  onChange={handleChange}
                  placeholder="e.g. 100"
                />
              </div>
              <div>
                <Label htmlFor="gre">GRE</Label>
                <Input
                  id="gre"
                  name="gre"
                  value={formData.gre}
                  onChange={handleChange}
                  placeholder="e.g. 320"
                />
              </div>
            </div>
          </Card>

          {/* Preferences */}
          <Card className="partner-card mb-6 rounded-[28px] p-6">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Preferences</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferredCountries">Preferred Countries</Label>
                <Input
                  id="preferredCountries"
                  name="preferredCountries"
                  value={formData.preferredCountries}
                  onChange={handleChange}
                  placeholder="e.g. USA, UK, Canada (comma-separated)"
                />
              </div>
              <div>
                <Label htmlFor="preferredDegreeLevel">Preferred Degree Level</Label>
                <select
                  id="preferredDegreeLevel"
                  name="preferredDegreeLevel"
                  value={formData.preferredDegreeLevel}
                  onChange={handleChange}
                  className="w-full rounded-2xl px-3 py-2 text-sm"
                >
                  <option value="">Select level</option>
                  <option value="bachelor">Bachelor's</option>
                  <option value="master">Master's</option>
                  <option value="phd">PhD</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="budget">Budget</Label>
                <Input
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder="e.g. $20,000 - $30,000"
                />
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="partner-secondary-btn rounded-2xl"
              onClick={() => navigate('/partner/students')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="partner-primary-btn rounded-2xl"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
              ) : (
                <><UserPlus className="w-4 h-4 mr-2" /> Create Student</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </PartnerLayout>
  );
}
